import { JSONContent } from "@tiptap/core";
import { normalizeKey } from "./reportInfoFieldAliases";

const nodeText = (node: JSONContent): string =>
  node.text ?? (node.content || []).map(nodeText).join(
    node.type === "paragraph" || node.type === "heading" ? "" : " ",
  );

const stageKey = (label: string): string | undefined => {
  const key = normalizeKey(label);
  if (key === "prestress") return "pre_FE";
  if (key === "6monthfieldexposure") return "post_FE_1";
  if (key === "12monthfieldexposure") return "post_FE_2";
  return undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Fills FE stage image slots in place for the serial named by the table.
 * Other image layouts continue to use the image-selection drawer.
 */
export const bindFeImageTable = (
  table: JSONContent,
  records: Record<string, unknown>[],
): JSONContent | null => {
  const rows = table.content || [];
  const stages = rows.flatMap((row, index) => {
    const key = stageKey(nodeText(row));
    return key ? [{ key, index }] : [];
  });
  if (stages.length !== 3) return null;

  const serialLabel = rows.map(nodeText)
    .map((text) => text.match(/^\s*Serial\s*Number\s*:\s*(.+?)\s*$/i))
    .find((match) => match !== null);
  if (!serialLabel) {
    throw new Error("The FE image table has no serial number.");
  }
  const serial = serialLabel[1].trim();
  const record = records.find((item) => String(item.serial_number).trim() === serial);
  if (!record) {
    throw new Error(`No FE images returned for serial number "${serial}".`);
  }

  const updatedRows = [...rows];
  for (const { key, index } of stages) {
    const group = record[key];
    if (!isRecord(group) || !Array.isArray(group.images)) {
      throw new Error(`Missing ${key} image data for serial number "${serial}".`);
    }
    const valueRow = rows[index + 1];
    const cell = valueRow?.content?.[0];
    if (
      !cell ||
      valueRow.content?.length !== 1 ||
      stageKey(nodeText(valueRow)) !== undefined
    ) {
      throw new Error(`No image slot found for ${key} in the FE image table.`);
    }
    const images = group.images.map((image: unknown): JSONContent => {
      if (typeof image !== "string" || !image.trim()) {
        throw new Error(`Invalid ${key} image for serial number "${serial}".`);
      }
      const raw = image.trim();
      const clean = raw.replace(/\s+/g, "");
      const src = /^(https?:\/\/|data:image\/)/i.test(raw)
        ? raw
        : `data:image/${clean.startsWith("iVBOR") ? "png" : "jpeg"};base64,${clean}`;
      return {
        type: "paragraph",
        attrs: cell.content?.[0]?.attrs,
        content: [{
          type: "image",
          attrs: {
            src,
            alt: `${serial} - ${key}`,
            width: "100%",
            alignment: "center",
          },
        }],
      };

    });
    updatedRows[index + 1] = {
      ...valueRow,
      content: [{
        ...cell,
        content: images.length ? images : [{ type: "paragraph", attrs: cell.content?.[0]?.attrs }],
      }],
    };
  }
  return { ...table, content: updatedRows };
};

export const isFeStageImageTable = (table: JSONContent): boolean =>
  (table.content || []).filter((row) => stageKey(nodeText(row)) !== undefined).length === 3;

export const bindSelectedFeImages = (
  table: JSONContent,
  records: Record<string, unknown>[],
  selected: { serial_number?: string; stage?: string; images: string[] }[],
): JSONContent | null => bindFeImageTable(table, records.map((record) => {
  const filtered = { ...record };
  ["pre_FE", "post_FE_1", "post_FE_2"].forEach((stage) => {
    const group = record[stage];
    if (!isRecord(group) || !Array.isArray(group.images)) return;
    filtered[stage] = {
      ...group,
      images: group.images.filter((image) => selected.some((item) =>
        item.serial_number === String(record.serial_number) &&
        item.stage === stage && item.images.includes(image),
      )),
    };
  });
  return filtered;
}));
